import { type QueryClient, useQuery } from "@tanstack/react-query";
import { organizationAuditLogContract } from "@common/contracts/organization-audit-log.contract";
import {
    OrganizationHistoryDailyCountsQuery,
    OrganizationHistoryDetailsQuery,
    OrganizationHistoryQuery,
} from "@common/types/auditLog";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { toNarrowedCounts } from "@components/activityTimeline/activityTimelineNarrowedCounts.lib";
import { toCountsChunkCacheOptions, toCountsChunkKey } from "./auditLog.api";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";
import { useChunkQueries } from "./useChunkQueries";

const organizationAuditLogContractClient = getBackendAPIClient(
    organizationAuditLogContract,
);

export const organizationAuditLogQueryKeys = {
    all: () => ["organization-audit-log"],
    history: (organizationId: string, query: OrganizationHistoryQuery) => [
        ...organizationAuditLogQueryKeys.all(),
        "history",
        organizationId,
        query,
    ],
    dailyCounts: (
        organizationId: string,
        query: OrganizationHistoryDailyCountsQuery,
    ) => [
        ...organizationAuditLogQueryKeys.all(),
        "daily-counts",
        organizationId,
        query,
    ],
    historyDetails: (
        organizationId: string,
        query: OrganizationHistoryDetailsQuery | null,
    ) => [
        ...organizationAuditLogQueryKeys.all(),
        "history-details",
        organizationId,
        query,
    ],
} as const;

/**
 * Marks every organization History and Activity query stale. Call it after
 * anything that reverts past changes: past counts chunks stay fresh for
 * minutes otherwise.
 */
export const invalidateOrganizationHistory = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({
        queryKey: organizationAuditLogQueryKeys.all(),
    });

interface UseOrganizationHistoryParams {
    organizationId: string;
    query: OrganizationHistoryQuery;
}

interface UseOrganizationHistoryDetailsParams {
    organizationId: string;
    query: OrganizationHistoryDetailsQuery | null;
}

export function useOrganizationHistory({
    organizationId,
    query,
}: UseOrganizationHistoryParams) {
    const queryKey = organizationAuditLogQueryKeys.history(
        organizationId,
        query,
    );
    return useQuery({
        queryKey,
        queryFn: async () => {
            const result =
                await organizationAuditLogContractClient.getOrganizationHistory(
                    {
                        params: { organizationId },
                        query,
                    },
                );
            return result.body;
        },
        enabled: !!organizationId,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}

interface UseOrganizationHistoryDailyCountsQueriesParams {
    organizationId: string;
    queries: readonly OrganizationHistoryDailyCountsQuery[];
}

/** One firm counts query per chunk, see `useChunkQueries`. */
export function useOrganizationHistoryDailyCountsQueries({
    organizationId,
    queries,
}: UseOrganizationHistoryDailyCountsQueriesParams) {
    return useChunkQueries({
        scopeId: organizationId,
        queries,
        toChunkKey: toCountsChunkKey,
        toQueryOptions: (query) => ({
            queryKey: organizationAuditLogQueryKeys.dailyCounts(
                organizationId,
                query,
            ),
            queryFn: async () => {
                const result =
                    await organizationAuditLogContractClient.getOrganizationHistoryDailyCounts(
                        {
                            params: { organizationId },
                            query,
                        },
                    );
                return result.body;
            },
            ...toCountsChunkCacheOptions(query),
        }),
        toPlaceholder: toNarrowedCounts,
    });
}

export function useOrganizationHistoryDetails({
    organizationId,
    query,
}: UseOrganizationHistoryDetailsParams) {
    return useQuery({
        queryKey: organizationAuditLogQueryKeys.historyDetails(
            organizationId,
            query,
        ),
        queryFn: async () => {
            if (!query) {
                throw new Error("History details query is required");
            }
            const result =
                await organizationAuditLogContractClient.getOrganizationHistoryDetails(
                    {
                        params: { organizationId },
                        query,
                    },
                );
            return result.body;
        },
        enabled: !!organizationId && query !== null,
    });
}
